import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { waUrl } from "../../config/bdk";

const IMAGES = Array.from({ length: 8 }, (_, i) => {
  const n = String(i + 1).padStart(2, "0");
  return {
    src: `${process.env.PUBLIC_URL}/img/post-demos/post-demo-${n}.webp`,
    alt: `Imagen publicitaria DEMO ${i + 1}`,
  };
});

const ImageExamples = () => {
  useReveal();
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const trackRef = useRef(null);

  const openAt = (i) => setLightboxIdx(i);
  const close = () => setLightboxIdx(-1);
  const prev = () =>
    setLightboxIdx((i) => (i - 1 + IMAGES.length) % IMAGES.length);
  const next = () => setLightboxIdx((i) => (i + 1) % IMAGES.length);

  // Keyboard shortcuts for lightbox
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <section
      id="ejemplos-imagenes"
      data-testid="section-ejemplos-imagenes"
      className="bdk-section"
    >
      <div className="container-bdk">
        <div className="max-w-[760px] mx-auto text-center mb-14 bdk-reveal bdk-reveal--up">
          <p className="kicker">Ejemplos de imágenes</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Imágenes publicitarias
            <br />
            para tu negocio.
          </h2>
          <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
            Estas imágenes DEMO muestran diferentes estilos de publicidad visual
            que podemos desarrollar para promociones, campañas, publicaciones,
            historias o anuncios digitales.
          </p>
        </div>

        {/* Desktop grid (≥ md) */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4">
          {IMAGES.map((img, i) => (
            <button
              key={i}
              type="button"
              data-testid={`gallery-card-${i}`}
              onClick={() => openAt(i)}
              className="group relative overflow-hidden rounded-[12px] bg-[#111218] cursor-zoom-in bdk-reveal bdk-reveal--up"
              style={{
                aspectRatio: "4 / 5",
                border: "1px solid var(--line)",
                "--reveal-delay": `${i * 60}ms`,
              }}
              aria-label={img.alt}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span
                className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "rgba(17,18,24,.55)" }}
              >
                <span
                  className="w-14 h-14 rounded-full grid place-items-center text-white shadow-xl"
                  style={{
                    background: "var(--red)",
                    boxShadow: "0 10px 30px rgba(217,0,32,.4)",
                  }}
                  aria-hidden
                >
                  <Eye size={22} />
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* Mobile carousel (< md) */}
        <div className="md:hidden relative">
          <div
            ref={trackRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5"
            style={{
              scrollbarWidth: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <style>{`.gallery-carousel::-webkit-scrollbar{display:none}`}</style>
            {IMAGES.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openAt(i)}
                data-testid={`gallery-slide-${i}`}
                className="flex-shrink-0 relative overflow-hidden rounded-[12px] bg-[#111218] cursor-zoom-in"
                style={{
                  width: "80vw",
                  maxWidth: "360px",
                  aspectRatio: "4 / 5",
                  scrollSnapAlign: "start",
                  border: "1px solid var(--line)",
                }}
                aria-label={img.alt}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Anterior"
              data-testid="gallery-prev"
              className="w-10 h-10 rounded-full grid place-items-center bg-white shadow"
              style={{ border: "1px solid var(--line)" }}
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Siguiente"
              data-testid="gallery-next"
              className="w-10 h-10 rounded-full grid place-items-center bg-white shadow"
              style={{ border: "1px solid var(--line)" }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Inline CTA */}
        <div
          className="mt-12 rounded-[14px] bg-white p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 bdk-reveal bdk-reveal--up"
          style={{ border: "1px solid var(--line)" }}
        >
          <div>
            <h3 className="font-display text-[24px] md:text-[26px] uppercase leading-tight">
              ¿Quieres imágenes así para tu negocio?
            </h3>
            <p className="text-[#666873] text-[13px] mt-2">
              Las personalizamos con el nombre, logo, promociones, servicios y
              estilo visual de tu negocio.
            </p>
          </div>
          <a
            href={waUrl(
              "Hola, vi los ejemplos de imágenes publicitarias y quiero información para crear imágenes para mi negocio."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="gallery-inline-cta"
            className="bdk-btn bdk-btn--primary flex-shrink-0"
          >
            Quiero imágenes para mi negocio
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx >= 0 && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] grid place-items-center px-4 py-8"
          style={{ background: "rgba(6,6,8,.94)" }}
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Cerrar"
            data-testid="lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 grid place-items-center rounded-full text-white"
            style={{ background: "rgba(255,255,255,.08)" }}
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Anterior"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full text-white"
            style={{ background: "rgba(255,255,255,.08)" }}
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Siguiente"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full text-white"
            style={{ background: "rgba(255,255,255,.08)" }}
          >
            <ChevronRight size={22} />
          </button>
          <img
            src={IMAGES[lightboxIdx].src}
            alt={IMAGES[lightboxIdx].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] object-contain rounded-[10px]"
          />
        </div>
      )}
    </section>
  );
};

export default ImageExamples;
